import { useEffect, useState } from 'react';

/**
 * `usePrefersReducedMotion` — the OS preference, read once and kept live.
 *
 * ## Why this exists at all, when `reset.css` already turns every transition
 * and animation to 0.01ms under `@media (prefers-reduced-motion: reduce)`
 *
 * That global rule is the real, production accessibility control (handoff,
 * Accessibility: "all animation respects prefers-reduced-motion") and this
 * hook does not replace it — every CSS transition and `@keyframes` added in
 * this task still carries no motion once the OS preference is set, with or
 * without a single line of JavaScript.
 *
 * The reason it exists anyway: **jsdom's CSSOM does not evaluate `@media
 * (prefers-reduced-motion: reduce)` against injected stylesheets at all** — a
 * unit test can mock `window.matchMedia`, but nothing in jsdom consults that
 * mock to decide whether a `@media` block in a `.module.css` file applies.
 * `getComputedStyle` on an element inside such a block returns the SAME
 * values whether or not the reduce query matches, which is exactly the kind
 * of assertion Global Constraint 12 calls vacuous. Every earlier task in this
 * plan that ships a CSS-only reduced-motion rule notes this in a comment
 * ("measured in Chromium") rather than asserting it in Vitest.
 *
 * Spec D-2 requires the assertion to exist, not to be deferred to an e2e run
 * that does not exist yet on this box. So the one new state transition this
 * task adds that has no earlier CSS-only precedent to fall back on — the
 * collapsed sidebar's brand-mark entrance (`Sidebar.tsx`) — reads this hook
 * and overrides its own `animation` inline when the preference is reduced.
 * That inline override is what `usePrefersReducedMotion.test.ts` and
 * `AppShell.test.tsx`'s brand-mark suite can see and mutation-test in jsdom;
 * the CSS `@keyframes` underneath is what a real browser runs the rest of the
 * time.
 */
const QUERY = '(prefers-reduced-motion: reduce)';

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    // SSR/test safety: a matchMedia-less environment prefers motion, not
    // stillness — the same "do nothing surprising by default" choice
    // AppearanceProvider makes for the colour scheme.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(QUERY);
    const onChange = () => {
      setReduced(list.matches);
    };
    // Read again on mount: a mock installed after this hook's initial state
    // computation (or a real preference changed between renders) must not be
    // missed.
    onChange();
    // `addEventListener` is the modern API; Safari < 14 needs `addListener`.
    // Both are exercised by usePrefersReducedMotion.test.ts.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => {
        list.removeEventListener('change', onChange);
      };
    }
    list.addListener(onChange);
    return () => {
      list.removeListener(onChange);
    };
  }, []);

  return reduced;
}
